// @ts-nocheck
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  Spacer,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { RGrid } from "@raiden/library/components/RGrid";
import { useConfigurationMenu } from "@raiden/library/hooks/useConfigurationMenu";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { CenteredContent } from "../../components/CenteredContent";
import { DataHandler } from "../../components/DataHandler";
import { Href } from "../../components/Href";
import { Icon } from "../../components/Icon";
import { SocialsCard } from "../../components/Socials/Card";
import { getLandingImage } from "../../helpers/getLandingImage";
import { MenuItem } from "../Layouts/PublicLayout/Footer/SocialNetworks/MenuItem";
import { MenuSearch } from "../Layouts/PublicLayout/Header/Search/Menu";
import { SearchResult } from "../Layouts/PublicLayout/Header/Search/Results";
import { Item } from "../Posts/Common/Search/Item";

/**
 * @typedef {object} Props
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>>} articlesResponse
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]>} eventCategoriesResponse
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>} eventsResponse
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/social/Post").Post[]>} socialsPostsResponse
 * @property {string} categoryId
 * @property {import("react").Dispatch<import("react").SetStateAction<string>>} setCategoryId
 */
/**
 * @param {Props} props
 */
export function Home(props) {
  const {
    articlesResponse,
    eventCategoriesResponse,
    eventsResponse,
    socialsPostsResponse,
    categoryId,
    setCategoryId,
  } = props;

  const { menu } = useConfigurationMenu({ alias: "social-networks" });

  const videoRef = useRef(null);

  const isMedium = useBreakpointValue({ base: true, lg: false });
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [moveX, setMoveX] = useState(0);

  const translate = useTranslate();

  // Change the scroll position of the events carousel
  useEffect(() => {
    document
      .getElementById("hstackMovable")
      ?.scrollTo({ left: moveX, behavior: "smooth" });
  }, [moveX]);

  return (
    <Box>
      <CenteredContent maxWidth="1920px">
        <Box position="relative">
          {(() => {
            const landingMedia = getLandingImage({
              isDesktop: !isMobile && !isMedium,
            });
            const isVideo = landingMedia.endsWith(".mp4");

            if (isVideo) {
              return (
                <video
                  ref={videoRef}
                  preload="auto"
                  style={{
                    width: "100%",
                    height: "480px",
                    objectFit: "cover",
                  }}
                  src={landingMedia}
                  autoPlay
                  muted
                  playsInline
                  alt=""
                  loading="lazy"
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      videoRef.current.playbackRate = 0.8;
                    }
                  }}
                />
              );
            }
            return (
              <Image
                width="auto"
                height="480px"
                objectFit="cover"
                src={landingMedia}
                alt=""
                loading="lazy"
              />
            );
          })()}

          <Flex
            position="absolute"
            px={{ base: ".5rem", md: "50px" }}
            direction="column"
            justifyContent="center"
            top="0"
            left="0"
            right="0"
            bottom="0">
            <Text
              mx="auto"
              maxWidth={{ base: "250px", sm: "full" }}
              variant="h1"
              align="center"
              fontWeight={500}
              color="white">
              <FormattedMessage defaultMessage="Bienvenue à La Bresse" />
            </Text>

            <VStack mt="1.5rem" spacing="1.5rem">
              <Box w="full" maxW={{ base: "full", lg: "600px" }}>
                <SearchResult />
              </Box>

              <MenuSearch />
            </VStack>
          </Flex>
        </Box>

        <Box px="1rem" py="2rem">
          <CenteredContent>
            <Text variant="h2">
              <FormattedMessage defaultMessage="Les actualités de votre ville" />
            </Text>

            <RGrid
              mt="1rem"
              gridGap="1rem"
              minCellWidth={isMedium ? "300px" : "250px"}
              display={{ base: "flex", md: "grid" }}
              flexDirection={{ base: "row", md: "column" }}
              overflowX="auto">
              <DataHandler
                swrResponse={articlesResponse}
                noResult={
                  <Text>
                    <FormattedMessage defaultMessage="Aucun article" />
                  </Text>
                }>
                {({ data: posts }) => (
                  <>
                    {posts.map((post, index) => {
                      if (!isMobile) {
                        return (
                          <Item
                            post={post}
                            isFirst={index === 0}
                            display={{
                              base: "block",
                              md: index === 0 ? "none" : "block",
                              lg: "block",
                            }}
                            key={post.id}
                          />
                        );
                      }
                      return (
                        <Box w="300px" key={post.id}>
                          <Item post={post} isFirst={index === 0} />
                        </Box>
                      );
                    })}
                  </>
                )}
              </DataHandler>
            </RGrid>

            <Center mt="1.5rem">
              <Href href={generateFrontPath({ id: "@front.articles" })}>
                <Button
                  rightIcon={
                    <Icon
                      icon="ms_chevron_right"
                      size="1.5rem"
                      color="gray.400"
                    />
                  }
                  variant="link"
                  p="0.5rem">
                  <Text variant="button-small">
                    <FormattedMessage defaultMessage="Voir toutes les actualités" />
                  </Text>
                </Button>
              </Href>
            </Center>
          </CenteredContent>
        </Box>

        <Box>
          <CenteredContent maxWidth="1920px">
            <Box
              pt="5rem"
              pb="3rem"
              backgroundImage={`url(${generateFrontPath({
                id: "@front.internal-assets",
                parameters: {
                  filePath: "montains-light.svg",
                },
                includeBasePath: true,
              })})`}
              backgroundSize="auto 100%"
              backgroundPosition="center">
              <Box px="1rem" py="1.5rem">
                <CenteredContent>
                  <Text variant="h2">
                    <FormattedMessage defaultMessage="L'agenda des manifestations" />
                  </Text>
                </CenteredContent>
              </Box>

              <Box px="1rem" py="1rem">
                <CenteredContent>
                  <HStack py=".2rem" spacing="1.5rem" overflowX="auto">
                    <DataHandler swrResponse={eventCategoriesResponse}>
                      {({ data: postsCategories }) => (
                        <>
                          <Text
                            fontSize="1.25rem"
                            fontWeight={400}
                            color={categoryId ? "brandPrimary.700" : "#396AB0"}
                            textDecoration={categoryId ? "none" : "underline"}
                            cursor="pointer"
                            onClick={() => setCategoryId("")}>
                            <FormattedMessage defaultMessage="Tout" />
                          </Text>

                          {postsCategories.map((postCategory) => (
                            <Text
                              fontSize="1.25rem"
                              fontWeight={400}
                              color={
                                categoryId === String(postCategory.id)
                                  ? "#396AB0"
                                  : "brandPrimary.700"
                              }
                              textDecoration={
                                categoryId === String(postCategory.id)
                                  ? "underline"
                                  : "none"
                              }
                              onClick={() =>
                                setCategoryId(String(postCategory.id))
                              }
                              cursor="pointer"
                              key={postCategory.id}>
                              {translate(postCategory.name)}
                            </Text>
                          ))}
                        </>
                      )}
                    </DataHandler>

                    <Spacer />

                    <HStack display={{ base: "none", md: "flex" }}>
                      <IconButton
                        icon={
                          <Icon
                            icon="ms_chevron_left"
                            color="brandPrimary.700"
                          />
                        }
                        variant="outline"
                        aria-label="Next"
                        borderColor="brandPrimary.700"
                        onClick={() => setMoveX(moveX - 260)}
                        isDisabled={moveX <= 0}
                      />

                      <IconButton
                        icon={
                          <Icon
                            icon="ms_chevron_right"
                            color="brandPrimary.700"
                          />
                        }
                        variant="outline"
                        aria-label="Previous"
                        borderColor="brandPrimary.700"
                        onClick={() => setMoveX(moveX + 260)}
                        isDisabled={
                          moveX >=
                          ((eventsResponse.data?.data ?? []).length - 4) * 277
                        }
                      />
                    </HStack>
                  </HStack>
                </CenteredContent>
              </Box>

              <Box px="1rem" py="1rem">
                <CenteredContent>
                  <HStack
                    id="hstackMovable"
                    spacing="1.5rem"
                    overflowX={{ base: "auto", md: "hidden" }}>
                    <DataHandler
                      swrResponse={eventsResponse}
                      noResult={
                        <Box w="1920px">
                          <Text
                            fontSize="1.25rem"
                            fontWeight={400}
                            textAlign="center">
                            <FormattedMessage defaultMessage="Aucun évènement n'a été trouvé" />
                          </Text>
                        </Box>
                      }>
                      {({ data: postsEvents }) => (
                        <>
                          {postsEvents.map((postEvent) => (
                            <Box w="277px" key={postEvent.id}>
                              <Item post={postEvent} />
                            </Box>
                          ))}
                        </>
                      )}
                    </DataHandler>
                  </HStack>
                </CenteredContent>
              </Box>

              <Center mt="1.5rem">
                <Href href={generateFrontPath({ id: "@front.events" })}>
                  <Button
                    rightIcon={
                      <Icon
                        icon="ms_chevron_right"
                        size="1.5rem"
                        color="gray.400"
                      />
                    }
                    variant="link"
                    p="0.5rem">
                    <Text variant="button-small">
                      <FormattedMessage defaultMessage="Voir tous les évènements" />
                    </Text>
                  </Button>
                </Href>
              </Center>
            </Box>
          </CenteredContent>
        </Box>

        <Box px="1rem" py="2rem">
          <CenteredContent>
            <Text variant="h2">
              <FormattedMessage defaultMessage="En direct des réseaux" />
            </Text>

            <DataHandler
              swrResponse={socialsPostsResponse}
              noResult={
                <Center>
                  <Text
                    w="100%"
                    fontSize="1.25rem"
                    fontWeight={400}
                    textAlign="center">
                    <FormattedMessage defaultMessage="Aucun évènement n'a été trouvé" />
                  </Text>
                </Center>
              }>
              {({ data: socialsPosts }) => (
                <RGrid
                  mt="1rem"
                  gridGap="1rem"
                  minCellWidth={isMedium ? "300px" : "250px"}
                  display={{ base: "flex", md: "grid" }}
                  flexDirection={{ base: "row", md: "column" }}
                  overflowX="auto">
                  {socialsPosts.map((socialPost, index) => (
                    <Box
                      key={socialPost.id}
                      display={{
                        base: "block",
                        md: index >= 4 ? "none" : "block",
                        lg: "block",
                      }}
                      pb=".5rem">
                      <SocialsCard socialPost={socialPost} />
                    </Box>
                  ))}
                </RGrid>
              )}
            </DataHandler>

            <Center mt="1rem">
              <Flex
                direction={{ base: "column", md: "row" }}
                align="center"
                textAlign="center"
                gap=".5rem">
                <Text fontSize="1rem" fontWeight={400}>
                  <FormattedMessage defaultMessage="Retrouvez toute l’actualité de votre mairie sur vos réseaux sociaux préférés" />
                </Text>

                <HStack>
                  {menu?.items?.map(
                    /** @type {import("@raiden/library/types/Configuration").ConfigurationMenuItem} */
                    (menuItem) => {
                      return (
                        <MenuItem
                          key={menuItem.id}
                          menuItem={menuItem}
                          iconOnly={true}
                          color="brandPrimary.700"
                          size="1rem"
                        />
                      );
                    },
                  )}
                </HStack>
              </Flex>
            </Center>
          </CenteredContent>
        </Box>
      </CenteredContent>
    </Box>
  );
}
